import isEmpty from 'lodash.isempty';

/**
 * Processes consent data from API response and extracts relevant client information
 * @param {Object} newConsent - API response containing consent data
 * @returns {Object} Processed consent data with client details
 */
export const processConsentData = (newConsent) => {
    const { currentClient, planningGroup, archivedGroupSummary, pendingInvite } = newConsent.data;

    return {
        currentClient,
        planningGroup,
        archivedGroupSummary,
        pendingInvite,
        rasActiveClient: currentClient?.rasActiveClient,
        numberOfClients: planningGroup?.clients?.length,
        activeInvite: planningGroup?.activeInvite,
        hasDocuments: planningGroup?.latestReleasedFinancialPlan || planningGroup?.latestReleasedStrategyComparison || planningGroup?.latestReleasedSelectedStrategy,
        pastEngagements: !isEmpty(planningGroup?.pastEngagements),
        documentsOnFile: planningGroup?.latestReleasedFinancialPlan,
        isPrimaryClient: currentClient?.clientType === 'PRIMARY' || currentClient?.clientType === null || currentClient?.clientType === undefined
    };
};

/**
 * Processes engagement status data and determines client engagement state
 * @param {Object} planningGroup - Planning group data from API
 * @returns {Object} Processed engagement status with flags and arrays
 */
export const processEngagementStatus = (planningGroup) => {
    const eStatusNotNull = planningGroup?.activeEngagement?.engagementStatus;
    const getEngagementStatuses = planningGroup?.activeEngagement?.engagementStatuses;
    const status = eStatusNotNull && planningGroup?.activeEngagement?.engagementStatuses[0].status;
    const effectiveDate = eStatusNotNull && planningGroup?.activeEngagement?.engagementStatus?.effectiveDate;

    const prospStatus = ['NEWP', 'KYCC', 'GLSC', 'RTQC', 'INTC', 'EXTC', 'MANC', 'FPAC', 'CNGN'];
    const addAccStatus = ['INTC', 'EXTC', 'MANC', 'CNGN'];

    return {
        status,
        effectiveDate,
        engagementStatus: { engagementStatus: status, effectiveDate },
        getEngagementStatuses,
        isProspStatus: prospStatus.includes(status),
        isProspClient: planningGroup?.activeEngagement?.engagementStatuses?.some(x => x.status === 'NEWP'),
        isAddAccStatus: addAccStatus.includes(status),
        prospStatuses: planningGroup?.activeEngagement?.engagementStatuses?.map(engStatus => engStatus.status),
        isCEInitiated: planningGroup?.activeEngagement?.initiatorType === 'REFERRAL_REP',
        prospStatus,
        addAccStatus
    };
};

/**
 * Determines the last completed section based on engagement status
 * @param {string} status - Current engagement status
 * @param {boolean} isProspStatus - Whether client is in prospect status
 * @param {boolean} isAddAccStatus - Whether client is in add account status
 * @returns {string|null} Last completed section name or null
 */
export const getLastCompletedSection = (status, isProspStatus, isAddAccStatus) => {
    if (isProspStatus) {
        if (status === 'NEWP') {
            return 'newp'
        }
        if (status === 'RTQC') {
            return 'rtq'
        }
        if (status === 'KYCC') {
            return 'kyc'
        }
        if (status === 'GLSC') {
            return 'goals'
        }
        if (status === 'FPAC' || isAddAccStatus) {
            return 'add-accounts'
        }
    }
    return null;
};

/**
 * Determines client type for analytics purposes
 * @param {Object} currentClient - Current client data
 * @returns {string} Client type ('client' or 'co-client')
 */
export const getAnalyticsClientType = (currentClient) => {
    const currClientTypeCheck = currentClient ? currentClient.clientType : null;
    if ((!currClientTypeCheck && sessionStorage.getItem('emailInv')) || (currClientTypeCheck && currClientTypeCheck !== 'PRIMARY')) {
        return 'co-client';
    }
    return 'client';
};

/**
 * Determines how the engagement was created
 * @param {boolean} isAdvisorLed - Whether engagement is advisor-led
 * @param {boolean} isCEInitiated - Whether engagement is CE-initiated
 * @returns {string} Engagement creation type
 */
export const getEngagementTypeCreation = (isAdvisorLed, isCEInitiated) => {
    if (isAdvisorLed) {
        return 'Advisor Initiated'
    }

    if (isCEInitiated) {
        return 'CE Initiated'
    }
    return 'Online Initiated';
};

/**
 * Converts client consent strings to boolean values
 * @param {Object} currentClient - Current client data
 * @returns {Object} Consent preselect object with boolean values
 */
export const getConsentPreselect = (currentClient) => ({
    consent401k: Boolean(Number(currentClient?.activeConsent?.consent401k)),
    consentYodlee: Boolean(Number(currentClient?.activeConsent?.consentYodlee)),
    consentTrust: Boolean(Number(currentClient?.activeConsent?.consentTrust))
});

/**
 * Determines if engagement is advisor-led
 * @param {boolean} isCEInitiated - Whether engagement is CE-initiated
 * @param {Array} getEngagementStatuses - Array of engagement statuses
 * @param {Object} planningGroup - Planning group data
 * @returns {boolean} Whether engagement is advisor-led
 */
export const getIsAdvisorLed = (isCEInitiated, getEngagementStatuses, planningGroup) => {
    return !isCEInitiated && getEngagementStatuses?.some(engStatus => engStatus?.status === 'PRCN')
        && planningGroup?.activeEngagement !== null
};

/**
 * Checks if all clients have consented to e-delivery
 * @param {Object} planningGroup - Planning group data
 * @returns {boolean} Whether all clients have e-delivery consent
 */
export const getAllClientsEdeliveryConsented = (planningGroup) => {
    return planningGroup?.clients?.every(client => client?.activeConsent?.consentEDelivery === '1');
};
